import PropTypes from "prop-types";
// React
import { useEffect, useState } from "react";

// MUI
import { styled } from "@mui/material/styles";
import { Tooltip, Stack, Switch, FormControlLabel, FormGroup } from "@mui/material";

const FONT_FAMILY = "'PTSansProNarrow', sans-serif";

const IOSSwitch = styled((props) => <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />)(({ theme }) => ({
	width: 44,
	height: 22,
	padding: 0,
	margin: "4px 12px !important",
	"& .MuiSwitch-switchBase": {
		padding: 0,
		margin: 1,
		transitionDuration: "80ms",

		"&.Mui-checked": {
			transform: "translateX(22px)",
			color: "#fff",
			"& + .MuiSwitch-track": {
				backgroundColor: theme.palette.mode === "dark" ? "#2ECA45" : "var(--gray-blue-color)",
				opacity: 1,
				border: 0,
			},
			"&.Mui-disabled + .MuiSwitch-track": {
				backgroundColor: "var(--disabled-and-stroke-color)",
			},
		},
		"&.Mui-focusVisible .MuiSwitch-thumb": {
			color: "#33cf4d",
			border: "6px solid #fff",
		},
		"&.Mui-disabled .MuiSwitch-thumb": {
			color: theme.palette.mode === "light" ? theme.palette.grey[100] : theme.palette.grey[paletteColorVariant600],
		},
		"&.Mui-disabled + .MuiSwitch-track": {
			opacity: 1,
			backgroundColor: "var(--disabled-and-stroke-color)",
		},
	},
	"& .MuiSwitch-thumb": {
		boxSizing: "border-box",
		width: 20,
		height: 20,
	},
	"& .MuiSwitch-track": {
		borderRadius: 13,
		backgroundColor: theme.palette.mode === "light" ? "var(--neutral-gray)" : "#39393D",
		opacity: 1,
		transition: theme.transitions.create(["background-color"], {
			duration: 200,
		}),
	},
}));

const getLabel = (checked, textChecked = "", textUnChecked = "") => {
	if (checked) {
		return textChecked;
	}
	return textUnChecked;
};

const getLabelPlacement = ({ labelPlacement = "", textRight = false }) => {
	// 'bottom' | 'end' | 'start' | 'top'
	if (textRight) {
		return "end";
	}
	if (!labelPlacement) {
		return "start";
	}
	return labelPlacement;
};

export default function CustomizedSwitches(props) {
	const [checked, setChecked] = useState(false);
	const { key: tooltipKey = "default_tooltip", ...tooltipProps } = props?.tooltip || {};

	const handleChange = () => {
		setChecked(!checked);

		if (props.onChange) {
			props.onChange(!checked);
		}

		if (props.isLastToCheck && !checked) {
			setChecked(false);
		}
	};

	useEffect(() => {
		setChecked(Boolean(props?.checked));
		return () => {
			setChecked(false);
		};
	}, [props]);

	const labelPlacement = getLabelPlacement({ labelPlacement: props?.labelPlacement || "", textRight: Boolean(props.textRight) });

	return (
		<Stack sx={{ visibility: props.hidden ? "hidden" : "inherit", height: props.hidden ? "0px" : "auto" }}>
			<Tooltip
				{...tooltipProps}
				key={tooltipKey}
				componentsProps={{
					tooltip: {
						className: "!font-regular-sans !text-[12px]",
					},
				}}
				arrow
			>
				<FormGroup className="form-switch" sx={props.sx}>
					<FormControlLabel
						labelPlacement={labelPlacement}
						sx={{
							"& .MuiTypography-root": {
								fontFamily: FONT_FAMILY,
								color: "var(--text-color)",
								fontSize: "14.5px",
								whiteSpace: "nowrap",
							},
						}}
						control={
							props.IOSxs ? (
								<IOSSwitchXS
									checked={checked}
									sx={{ m: 1 }}
									inputProps={{ "aria-label": "controlled" }}
									disabled={props.disabled}
									onChange={handleChange}
								/>
							) : (
								<IOSSwitch
									checked={checked}
									sx={{ m: 1 }}
									inputProps={{ "aria-label": "controlled" }}
									disabled={props.disabled}
									onChange={handleChange}
								/>
							)
						}
						label={getLabel(checked, props.textChecked, props.textUnChecked)}
					/>
					<Stack direction="row" spacing={1} alignItems="center"></Stack>
				</FormGroup>
			</Tooltip>
		</Stack>
	);
}

const darkOpacity = 0.7;
const lightOpacity = 0.3;
const paletteColorVariant600 = 600;

const IOSSwitchXS = styled((props) => <Switch disableRipple {...props} />)(({ theme }) => ({
	width: 30,
	height: 16,
	padding: 0,

	"& .MuiSwitch-switchBase": {
		padding: 1,
		margin: 0,
		transitionDuration: "80ms",
		marginLeft: 0,

		"&.Mui-checked": {
			transform: "translateX(14px)",
			color: "#fff",
			"& + .MuiSwitch-track": {
				backgroundColor: theme.palette.mode === "dark" ? "#2ECA45" : "var(--gray-blue-color)",
				opacity: 1,
				border: 0,
			},
			"&.Mui-disabled + .MuiSwitch-track": {
				opacity: 0.5,
			},
		},
		"&.Mui-focusVisible .MuiSwitch-thumb": {
			color: "#33cf4d",
			border: "6px solid #fff",
		},
		"&.Mui-disabled .MuiSwitch-thumb": {
			color: theme.palette.mode === "light" ? theme.palette.grey[100] : theme.palette.grey[paletteColorVariant600],
		},
		"&.Mui-disabled + .MuiSwitch-track": {
			opacity: theme.palette.mode === "light" ? darkOpacity : lightOpacity,
		},
	},
	"& .MuiSwitch-thumb": {
		boxSizing: "border-box",
		width: 14,
		height: 14,
	},
	"& .MuiSwitch-track": {
		borderRadius: 13,
		backgroundColor: theme.palette.mode === "light" ? "var(--neutral-gray)" : "#39393D",
		opacity: 1,
		transition: theme.transitions.create(["background-color"], {
			duration: 200,
		}),
	},
}));

CustomizedSwitches.propTypes = {
	checked: PropTypes.bool,
	onChange: PropTypes.func,
	textChecked: PropTypes.oneOfType([PropTypes.node, PropTypes.object, PropTypes.string]),
	textUnChecked: PropTypes.oneOfType([PropTypes.node, PropTypes.object, PropTypes.string]),
	textRight: PropTypes.bool,
	IOSxs: PropTypes.bool,
	disabled: PropTypes.bool,
	hidden: PropTypes.bool,
	isLastToCheck: PropTypes.bool,
	sx: PropTypes.object,
	className: PropTypes.string,
	tooltip: PropTypes.object,
};
